import styles from "@/styles/CatalogueCell.module.css";
import { Castle, Game } from "./Database";
import { Inter } from "next/font/google";
import { useRouter } from "next/router";
import { useCallback, useEffect, useRef, useState } from "react";
import { X } from "lucide-react";

const inter = Inter({ subsets: ["latin"] });

type CatalogueChoiceCellProps = {
  type: string;
  content: Castle | Game;
};

export default function CatalogueChoiceCell({ type, content }: CatalogueChoiceCellProps) {
  const router = useRouter();
  const nl = router.asPath.includes("/nl/");
  const fr = router.asPath.includes("/fr/");

  const [firstChoice, setFirstChoice] = useState<string | null>(null);
  const [secondChoice, setSecondChoice] = useState<string | null>(null);

  const [currentImage, setCurrentImage] = useState(0);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const images = [content.imagePath];

  const [promotionVariable, setPromotionVariable] = useState<boolean | null>(null);

  const firstButtonRef = useRef<HTMLButtonElement>(null);
  const secondButtonRef = useRef<HTMLButtonElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const intervalId = setInterval(() => {
      const newFirstValue = sessionStorage.getItem("firstChoiceNL");
      const newSecondValue = sessionStorage.getItem("secondChoiceNL");
      const newPromotionVariable = sessionStorage.getItem("specialPromo");
      if (newFirstValue !== firstChoice) {
        setFirstChoice(newFirstValue);
      }
      if (newSecondValue !== secondChoice) {
        setSecondChoice(newSecondValue);
      }
      if (newPromotionVariable !== promotionVariable) {
        setPromotionVariable(newPromotionVariable === "TRUE");
      }
    }, 100);
    return () => clearInterval(intervalId);
  }, [firstChoice, secondChoice, promotionVariable]);

  useEffect(() => {
    if (isViewerOpen) {
      document.body.style.overflow = "hidden"; // Disable scrolling
    } else {
      document.body.style.overflow = ""; // Enable scrolling
    }
    return () => {
      document.body.style.overflow = ""; // Re-enable scrolling when component unmounts
    };
  }, [isViewerOpen]);

  // Check clicked outside, close
  useEffect(() => {
    const handleOutSideClick = (event: Event) => {
      if (event.target != null && !imageRef.current?.contains(event.target as Node)) {
        setIsViewerOpen(false);
      }
    };
    window.addEventListener("mousedown", handleOutSideClick);
    return () => {
      window.removeEventListener("mousedown", handleOutSideClick);
    };
  }, [imageRef]);

  const openImageViewer = useCallback((index: number) => {
    setCurrentImage(index);
    setIsViewerOpen(true);
  }, []);

  const closeImageViewer = () => {
    setCurrentImage(0);
    setIsViewerOpen(false);
  };

  function firstButtonText() {
    if (nl) {
      return <>Eerste keuze</>;
    } else if (fr) {
      return <>Premier choix</>;
    }
  }

  function secondButtonText() {
    if (nl) {
      return <>Tweede keuze</>;
    } else if (fr) {
      return <>Deuxième choix</>;
    }
  }

  function firstChoiceClicked() {
    if (sessionStorage.getItem("multipleCastles") !== null) {
      sessionStorage.removeItem("multipleCastles");
    }
    if (firstChoice === content.nameNL) {
      sessionStorage.removeItem("firstChoiceNL");
      sessionStorage.removeItem("firstChoiceFR");
    } else {
      sessionStorage.setItem("firstChoiceNL", content.nameNL);
      sessionStorage.setItem("firstChoiceFR", content.nameFR);
    }
  }

  function secondChoiceClicked() {
    if (sessionStorage.getItem("multipleCastles") !== null) {
      sessionStorage.removeItem("multipleCastles");
    }
    if (secondChoice === content.nameNL) {
      sessionStorage.removeItem("secondChoiceNL");
      sessionStorage.removeItem("secondChoiceFR");
    } else {
      sessionStorage.setItem("secondChoiceNL", content.nameNL);
      sessionStorage.setItem("secondChoiceFR", content.nameFR);
    }
  }

  function showFirstChoiceButton() {
    if (
      (firstChoice != null && firstChoice != content.nameNL) ||
      (secondChoice != null && secondChoice === content.nameNL)
    ) {
      return <button disabled>{firstButtonText()}</button>;
    } else if (firstChoice != null && firstChoice === content.nameNL) {
      return (
        <button
          className={styles.rentButton}
          ref={firstButtonRef}
          onClick={() => firstChoiceClicked()}
          style={{ backgroundColor: "#4D7EA8", color: "white" }}
        >
          {firstButtonText()}
        </button>
      );
    } else {
      return (
        <button
          className={styles.rentButton}
          ref={firstButtonRef}
          style={{ backgroundColor: "#f0f0f0", color: "black" }}
          onClick={() => firstChoiceClicked()}
        >
          {firstButtonText()}
        </button>
      );
    }
  }

  function showSecondChoiceButton() {
    if (
      (secondChoice != null && secondChoice != content.nameNL) ||
      (firstChoice != null && firstChoice === content.nameNL)
    ) {
      return <button disabled>{secondButtonText()}</button>;
    } else if (secondChoice != null && secondChoice === content.nameNL) {
      return (
        <button
          className={styles.rentButton}
          ref={secondButtonRef}
          onClick={() => secondChoiceClicked()}
          style={{ backgroundColor: "#4D7EA8", color: "white" }}
        >
          {secondButtonText()}
        </button>
      );
    } else {
      return (
        <button
          className={styles.rentButton}
          ref={secondButtonRef}
          style={{ backgroundColor: "#f0f0f0", color: "black" }}
          onClick={() => secondChoiceClicked()}
        >
          {secondButtonText()}
        </button>
      );
    }
  }

  if (type === "rent") {
    return (
      <div id={content.nameNL} className={styles.catalogueCell}>
        {nl && (
          <>
            {content.imagePath ? (
              <>
                <img
                  src={content.imagePath}
                  alt="Foto van springkasteel"
                  width={300}
                  height={300}
                  onClick={() => openImageViewer(0)}
                  style={{ cursor: "pointer" }}
                />
                {isViewerOpen && (
                  <div className={styles.imageViewer}>
                    <div className={styles.wrapper}>
                      <button className={styles.closeButton} onClick={closeImageViewer}>
                        <X size={40} />
                      </button>
                      <div className={styles.content}>
                        <img
                          src={images[currentImage]}
                          alt="Afbeelding van een springkasteel"
                          width={300}
                          height={300}
                          style={{ maxWidth: "none", maxHeight: "none" }}
                          ref={imageRef}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div style={{ width: "300px", height: "300px" }}></div>
            )}
            <p className={styles.name}>{content.nameNL}</p>
            <p className={styles.extraInfo}>{content?.extraNL}</p>
            <p className={styles.dimensions}>
              B: {content.width}m &ndash; D: {content.depth}m &ndash; H: {content.height}m
            </p>
            <p className={styles.price}>&euro;{content.price} / dag*</p>
            {promotionVariable !== null && promotionVariable && (
              <div className={styles.promoBox}>
                <div className={styles.oldPrice}>
                  <p className={styles.scratchedPrice}>&euro;{(content as Castle).promotion}</p>
                  <p className={styles.promoDays}>/ 2 dagen*</p>
                </div>
                <div className={styles.specialPromo}>
                  <p className={styles.promo}>&euro;{(content as Castle).price}</p>
                  <p className={styles.promoText}>PROMO</p>
                </div>
              </div>
            )}
            {promotionVariable !== null && !promotionVariable && (
              <p className={styles.promo}>&euro;{(content as Castle).promotion} / 2 dagen*</p>
            )}
          </>
        )}
        {fr && (
          <>
            {content.imagePath ? (
              <>
                <img
                  src={content.imagePath}
                  alt="Photo d'un château gonflable"
                  width={300}
                  height={300}
                  onClick={() => openImageViewer(0)}
                  style={{ cursor: "pointer" }}
                />
                {isViewerOpen && (
                  <div className={styles.imageViewer}>
                    <div className={styles.wrapper}>
                      <button className={styles.closeButton} onClick={closeImageViewer}>
                        <X size={40} />
                      </button>
                      <div className={styles.content}>
                        <img
                          src={images[currentImage]}
                          alt="Photo d'un château gonflable"
                          width={300}
                          height={300}
                          style={{ maxWidth: "none", maxHeight: "none" }}
                          ref={imageRef}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div style={{ width: "300px", height: "300px" }}></div>
            )}
            <p className={styles.name}>{content.nameFR}</p>
            <p className={styles.extraInfo}>{content?.extraFR}</p>
            <p className={styles.dimensions}>
              L: {content.width}m &ndash; P: {content.depth}m &ndash; H: {content.height}m
            </p>
            <p className={styles.price}>&euro;{content.price} / jour</p>
            {promotionVariable !== null && promotionVariable && (
              <div className={styles.promoBox}>
                <div className={styles.oldPrice}>
                  <p className={styles.scratchedPrice}>&euro;{(content as Castle).promotion}</p>
                  <p className={styles.promoDays}>/ 2 jours</p>
                </div>
                <div className={styles.specialPromo}>
                  <p className={styles.promo}>&euro;{(content as Castle).price}</p>
                  <p className={styles.promoText}>PROMO</p>
                </div>
              </div>
            )}
            {promotionVariable !== null && !promotionVariable && (
              <p className={styles.promo}>&euro;{(content as Castle).promotion} / 2 jours</p>
            )}
          </>
        )}
        <div className={styles.buttons}>
          {showFirstChoiceButton()}
          {showSecondChoiceButton()}
        </div>
      </div>
    );
  } else if (type === "buy") {
    return (
      <div id={content.nameNL} className={styles.catalogueCell}>
        {nl && (
          <>
            {content.imagePath ? (
              <>
                <img
                  src={content.imagePath}
                  alt="Foto van springkasteel"
                  width={300}
                  height={300}
                  onClick={() => openImageViewer(0)}
                  style={{ cursor: "pointer" }}
                />
                {isViewerOpen && (
                  <div className={styles.imageViewer}>
                    <div className={styles.wrapper}>
                      <button className={styles.closeButton} onClick={closeImageViewer}>
                        <X size={40} />
                      </button>
                      <div className={styles.content}>
                        <img
                          src={images[currentImage]}
                          alt="Afbeelding van een springkasteel"
                          width={300}
                          height={300}
                          style={{ maxWidth: "none", maxHeight: "none" }}
                          ref={imageRef}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div style={{ width: "300px", height: "300px" }}></div>
            )}
          </>
        )}
        {fr && (
          <>
            {content.imagePath ? (
              <>
                <img
                  src={content.imagePath}
                  alt="Photo d'un château gonflable"
                  width={300}
                  height={300}
                  style={{ cursor: "pointer" }}
                  onClick={() => openImageViewer(0)}
                />
                {isViewerOpen && (
                  <div className={styles.imageViewer}>
                    <div className={styles.wrapper}>
                      <button className={styles.closeButton} onClick={closeImageViewer}>
                        <X size={40} />
                      </button>
                      <div className={styles.content}>
                        <img
                          src={images[currentImage]}
                          alt="Photo d'un château gonflable"
                          width={300}
                          height={300}
                          style={{ maxWidth: "none", maxHeight: "none" }}
                          ref={imageRef}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div style={{ width: "300px", height: "300px" }}></div>
            )}
          </>
        )}
      </div>
    );
  }
}
